/**
 * SymbolAutocomplete — reusable symbol search field
 * Auto-uppercases input, queries /api/instruments, shows Symbol + Exchange chip.
 * Use this everywhere in the app that needs a symbol selection field.
 */
import React, { useState, useCallback, useRef } from 'react';
import { Autocomplete, TextField, Chip, Box, Typography, CircularProgress } from '@mui/material';
import api from '../../services/api';
import { useAppTheme } from '../../hooks/useAppTheme';

export interface SymbolOption {
  symbol: string;
  exchange: string;
  name?: string;
  lotSize?: number;
  instrumentType?: string;
}

interface Props {
  label?: string;
  value: string;
  onChange: (option: SymbolOption | null) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  /** Extra filter to narrow results (e.g. only show NFO instruments) */
  exchangeFilter?: string;
}

const EXCHANGE_COLORS_LIGHT: Record<string, { bg: string; color: string }> = {
  NSE:   { bg: '#e8f5e9', color: '#1b5e20' },
  NFO:   { bg: '#e3f2fd', color: '#0d47a1' },
  BSE:   { bg: '#fff3e0', color: '#e65100' },
  MCX:   { bg: '#f3e5f5', color: '#4a148c' },
  DELTA: { bg: '#e0f7fa', color: '#006064' },
};
const EXCHANGE_COLORS_DARK: Record<string, { bg: string; color: string }> = {
  NSE:   { bg: 'rgba(45,212,170,0.15)',  color: '#2DD4AA' },
  NFO:   { bg: 'rgba(96,165,250,0.15)',  color: '#60A5FA' },
  BSE:   { bg: 'rgba(251,191,36,0.15)',  color: '#FBBF24' },
  MCX:   { bg: 'rgba(167,139,250,0.15)', color: '#A78BFA' },
  DELTA: { bg: 'rgba(96,165,250,0.12)',  color: '#60A5FA' },
};

const SymbolAutocomplete: React.FC<Props> = ({
  label = 'Symbol',
  value,
  onChange,
  size = 'small',
  disabled = false,
  placeholder = 'Type to search…',
  fullWidth = true,
  exchangeFilter,
}) => {
  const { isDark } = useAppTheme();
  const [options, setOptions] = useState<SymbolOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback((term: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!term.trim()) { setOptions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params: Record<string, any> = { search: term, pageSize: 20 };
        if (exchangeFilter) params.exchange = exchangeFilter;
        const r = await api.get('/instruments', { params });
        const rData = (r as any).data;
        const items: any[] = rData?.data?.instruments || rData?.instruments || [];
        setOptions(items.map((inst: any) => ({
          symbol: inst.symbol,
          exchange: inst.exchange || '',
          name: inst.name || '',
          lotSize: inst.lotSize ? Number(inst.lotSize) : undefined,
          instrumentType: inst.instrumentType || '',
        })));
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [exchangeFilter]);

  const exchStyle = (exchange: string) => {
    const map = isDark ? EXCHANGE_COLORS_DARK : EXCHANGE_COLORS_LIGHT;
    return map[exchange] || (isDark ? { bg: 'rgba(110,118,129,0.15)', color: '#8B949E' } : { bg: '#f5f5f5', color: '#333' });
  };

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      options={options}
      loading={loading}
      filterOptions={(x) => x}  // server-side filtering only
      getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.symbol}
      isOptionEqualToValue={(opt, val) =>
        typeof val === 'string'
          ? opt.symbol === val
          : opt.symbol === val.symbol && opt.exchange === val.exchange
      }
      inputValue={inputValue}
      onInputChange={(_e, val, reason) => {
        const upper = val.toUpperCase();
        setInputValue(upper);
        if (reason === 'input') search(upper);
        if (reason === 'clear') { setOptions([]); onChange(null); }
      }}
      onChange={(_e, val) => {
        const v = val as SymbolOption | null;
        if (!v) {
          onChange(null);
        } else {
          setInputValue(v.symbol);
          onChange(v);
        }
      }}
      renderOption={(props, opt) => {
        const { key, ...rest } = props as any;
        const style = exchStyle(opt.exchange);
        return (
          <Box component="li" key={`${opt.symbol}-${opt.exchange}`} {...rest}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
              {opt.symbol}
            </Typography>
            <Chip
              label={opt.exchange}
              size="small"
              sx={{ fontSize: '0.68rem', height: 18, bgcolor: style.bg, color: style.color, fontWeight: 600 }}
            />
            {opt.name && (
              <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {opt.name}
              </Typography>
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={14} sx={{ mr: 1 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SymbolAutocomplete;
